import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, SectionList, FlatList, ActivityIndicator } from 'react-native-web';
import moment from 'moment';
import * as Animatable from 'react-native-animatable';
import { cloneDeep } from 'lodash';
import { Bounce } from 'react-native-animated-spinkit';
import Lottie from 'react-lottie';
import { useRoute } from '@react-navigation/native';

// Custom Components
import { Button, EmptyTextSection, SceneCateringClaimStatusTag, SceneCateringTagItem, TierBlockView } from '../../components';

// Lotties
import * as CateringLottie from '../../lotties/lottie_catering.json';
import * as HoodLottie from '../../lotties/lottie_hood.json';
import * as EmptyCalendarLottie from '../../lotties/lottie_empty_calendar.json';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import RouteKeys from '../../navigation/RouteKeys';

// Configs
import GLOBAL from '../../configs/config_global';
import DATA from '../../configs/config_data';
import { getGlobalState, setGlobalState, STATE_KEYS, useGlobalState } from '../../configs/config_state';

// APIs
import { checkDate } from '../../modules/converters';
import { getCateringList, getHoodList } from '../../modules/api_catering';

// Utils
import { isCateringMissed, isHired, isNotHired } from '../../utils/util_catering';
import { isFeedbackNeeded } from '../../utils/util_catering';
import { isObject, isTestAccount, showAlert, showAppError } from '../../utils/util_helpers';
import { isProfilePendingApproval } from '../../utils/util_truck_helpers';
import LoadingView from '../../components/component_loading_view';
import Feather from 'react-native-vector-icons/dist/Feather';

// Moment Setup
const momentDurationFormatSetup = require('moment-duration-format');
momentDurationFormatSetup(moment);

export function Catering() {
    // region Values
    const hasCateringAccess = GLOBAL.DATA.USER['tier'] > 0;
    const hasHoodAccess = GLOBAL.DATA.USER['tier'] > 1;

    const cateringCategories = {
        awaiting: {
            title: 'Pending',
            data: [],
            color: GLOBAL.COLOR.BASIC.UI_YELLOW
        },
        upcoming: {
            title: 'Accepted',
            data: [],
            color: GLOBAL.COLOR.BASIC.UI_BLUE
        },
        hired: {
            title: 'Hired',
            data: [],
            color: GLOBAL.COLOR.BASIC.UI_GREEN
        },
        notHired: {
            title: 'Not Hired',
            data: [],
            color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY
        },
        missed: {
            title: 'Missed',
            data: [],
            color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY
        }
    };

    const cateringHistoryCategories = {
        upcoming: {
            title: 'Accepted',
            data: [],
            color: GLOBAL.COLOR.BASIC.UI_BLUE
        },
        hired: {
            title: 'Hired',
            data: [],
            color: GLOBAL.COLOR.BASIC.UI_GREEN
        },
        notHired: {
            title: 'Not Hired',
            data: [],
            color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY
        }
    };
    // endregion

    // region Refs
    const cateringTab = useRef(null);
    const hoodTab = useRef(null);
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [pastCateringListObj, setPastCateringListObj] = useGlobalState(STATE_KEYS.PAST_CATERING_LIST_OBJ);
    const [pastHoodData, setPastHoodData] = useGlobalState(STATE_KEYS.PAST_HOOD_DATA);
    const [isDevMode] = useGlobalState(STATE_KEYS.IS_DEV);
    const [cateringScreenKey] = useGlobalState(STATE_KEYS.CATERING_SCREEN_KEY);
    const [triggerCateringResponded, setTriggerCateringResponded] = useGlobalState(STATE_KEYS.TRIGGER_CATERING_RESPONDED);
    const [cateringDeepLinkData, setCateringDeepLinkData] = useGlobalState(STATE_KEYS.DEEP_LINK_DATA_CATERING);
    const [currentScreenKey] = useGlobalState(STATE_KEYS.CURRENT_SCREEN_KEY);
    // endregion

    // region Local State
    const [isLoading, setLoading] = useState(false);
    const [cateringData, setCateringData] = useState([]);
    const [pastCateringData, setPastCateringData] = useState([]);

    const [hoodData, setHoodData] = useState([]);

    const [upcomingCateringListObj, setUpcomingCateringListObj] = useState({});

    const [feedbackNeedCount, setFeedbackNeedCount] = useState(0);
    const [hoodFeedbackNeedCount, setHoodFeedbackNeedCount] = useState(0);

    const [cateringTabActive, setCateringTabActive] = useState(true);

    const [dataFetched, setDataFetched] = useState(false);

    const [pastAcceptedLeadCount, setPastAcceptedLeadCount] = useState(0);
    // endregion

    // region Component Mounting
    useEffect(() => {
        goFetchCateringData();
    }, []);

    useEffect(() => {
        if (currentScreenKey.includes('Catering-')) {
            setGlobalState(STATE_KEYS.CATERING_SCREEN_KEY, currentScreenKey);
        }
    }, [currentScreenKey]);
    // endregion

    // region Function - Go Deep Link
    const goDeepLink = () => {
        const { goTo = '', openDetails = {} } = cateringDeepLinkData || {};

        // Reset params
        setCateringDeepLinkData({ goTo: '', openDetails: { dataID: null, isHood: null } });

        if (goTo?.trim()) {
            const goToLink = goTo;

            console.log('deep goToLink', goToLink);

            // Go to the requested link
            switch(goToLink) {
                case 'catering-reminder': {
                    setCateringTabActive(true);
                    cateringTab.current?.fadeZoom(600);
                    break;
                }
                case 'hood-reminder': {
                    setCateringTabActive(false);
                    hoodTab.current?.fadeZoom(600);
                    break;
                }
                case 'hood-reminder-past': {
                    _onPastButtonPressed(true);
                    break;
                }
                case 'catering-reminder-past': {
                    _onPastButtonPressed(false);
                    break;
                }
                default:
                    break;
            }
        }
        else if (openDetails.dataID) {
            console.log('deep openDetails', openDetails);

            const dataID = openDetails.dataID;
            const isHood = openDetails.isHood;

            // Open details screen
            if (isHood) {
                _onHoodItemPressed({ id: dataID });
            }
            else {
                _onCateringItemPressed({ id: dataID });
            }
        }
    };
    // endregion

    // region Watcher - Catering Deep Link
    useEffect(() => {
        if (dataFetched && (cateringDeepLinkData?.goTo?.trim() || cateringDeepLinkData?.openDetails?.dataID)) {
            console.log('deep fetched cateringDeepLinkData', cateringDeepLinkData);
            goDeepLink();
        }
    }, [cateringDeepLinkData, dataFetched]);
    // endregion

    // region Function - Categorize Catering Data
    const categorizeCateringData = (dataArr = [], isHistory = false) => {
        // Generate calendar object
        const cateringListObj = cloneDeep(isHistory ? cateringHistoryCategories : cateringCategories);

        // Start processing each catering item
        dataArr.forEach((item) => {
            // Categorize catering leads
            /* Hired */
            if (isHired(item, globalTruckData?.id)) {
                cateringListObj['hired']?.data.push(item);
            }
            else {
                /* Not Hired */
                if (isNotHired(item, globalTruckData?.id)) {
                    cateringListObj['notHired']?.data.push(item);
                    return;
                }

                /* Accepted */
                if (item['child_status'] === 1) {
                    cateringListObj['upcoming']?.data.push(item);
                }
                else {
                    /* Filled or Expired */
                    if (isCateringMissed(item)) {
                        cateringListObj['missed']?.data.push(item);
                    }
                    else {
                        /* Pending */
                        if (!isHistory) {
                            cateringListObj['awaiting']?.data.push(item);
                        }
                    }
                }
            }
        });

        console.log(`[INFO] Catering List Object (${isHistory ? 'PAST' : 'UPCOMING'})`, cateringListObj);

        return(cateringListObj);
    };
    // endregion

    // region Function - Sort Catering Data
    const sortCateringData = (dataList = [], isHood = false) => {
        const timeArr = dataList.map((item) => {
            const tempObj = cloneDeep(item);
            const thisTruckData = isHood ? item['truck'].find((truck) => truck.id === globalTruckData?.id) : null;

            return(
                Object.assign(
                    tempObj,
                    { thisTruckData, timeObj: checkDate(tempObj, isHood ? 'UTC' : true) },
                    isHood ?
                        {
                            confirmed_status: thisTruckData['confirmed_status'],
                            weather: thisTruckData['weather']
                        }
                        :
                        {},
                )
            );
        });

        return({
            upcoming: timeArr.filter((item) => (item.timeObj?.endTime >= moment().unix())).sort((a, b) => (a.timeObj.startTime - b.timeObj.startTime)),
            history: timeArr
                .filter((item) => (item.timeObj?.endTime < moment().unix()))
                .sort((a, b) => (b.timeObj.startTime - a.timeObj.startTime))
                .sort((a, b) => (Number(isFeedbackNeeded(b)) - Number(isFeedbackNeeded(a))))

        });
    };
    // endregion

    // region Function - Count Feedback Needed Leads
    const getFeedbackNeededLeads = (hiredArr = []) => {
        let feedbackNeedCount = 0;
        hiredArr.forEach((item) => isFeedbackNeeded(item) && (feedbackNeedCount += 1));
        return(feedbackNeedCount);
    };
    // endregion

    // region Function - Fetch Catering Data
    const goFetchCateringData = () => {
        if (isLoading) {
            return;
        }

        setLoading(true);
        Promise.all([
            getCateringList(globalTruckData?.id, 1, 200),
            (GLOBAL.DATA.USER['tier'] > 1) ? getHoodList(globalTruckData?.id, 1, 100).catch((errorMessage) => Promise.resolve(`Unable to fetch Hood data.<br/><br/>Error message:<br/>${errorMessage}`)) : Promise.resolve([])
        ])
            .then((resultArr) => {
                const cateringResult = resultArr[0], hoodResult = isObject(resultArr[1]) ? resultArr[1] : { data: [] };

                // Sort calendar in order to detect multiple-day events
                const cateringArr = sortCateringData(cateringResult.data);
                const upcomingCateringArr = cateringArr.upcoming;
                const pastCateringArr = cateringArr.history;
                const hoodArr = sortCateringData(hoodResult.data, true);

                // Count number of feedback needed leads
                const newPastCateringListObj = categorizeCateringData(pastCateringArr, true);
                const newFeedbackNeedCount = getFeedbackNeededLeads(newPastCateringListObj['hired'].data);
                const newHoodFeedbackNeedCount = getFeedbackNeededLeads(hoodArr.history);

                // Set data to state
                setCateringData(cateringResult.data);
                setPastCateringData(pastCateringArr);
                setUpcomingCateringListObj(categorizeCateringData(upcomingCateringArr, false));
                setPastCateringListObj(newPastCateringListObj);
                setFeedbackNeedCount(newFeedbackNeedCount);
                setHoodData(hoodArr.upcoming);
                setPastHoodData(hoodArr.history);
                setHoodFeedbackNeedCount(newHoodFeedbackNeedCount);
                setPastAcceptedLeadCount(newPastCateringListObj['upcoming'].data.length);

                // Show error if exists
                if (typeof resultArr[1] === 'string') {
                    showAlert({ text: resultArr[1] });
                }

                // Set data fetched
                setDataFetched(true);
            })
            .catch((error) => showAppError(error))
            .finally(() => setLoading(false));
    };
    // endregion

    // region Function - On External Lead Created
    const onExternalLeadCreated = (cateringItem) => {
        const tempCalendarListObj = cloneDeep(upcomingCateringListObj);
        const tempCateringData = cloneDeep(cateringData);

        // Push to general list
        tempCateringData.push(cateringItem);

        // Push to Pending list
        tempCalendarListObj['upcoming']?.data.push(cateringItem);
        tempCalendarListObj['upcoming']?.data.sort((a, b) => a.timeObj.startTime - b.timeObj.startTime);

        // Save to state
        setUpcomingCateringListObj(tempCalendarListObj);
        setCateringData(tempCateringData);
    };
    // endregion

    // region Function - On Catering Accept/Decline/Hired
    const onCateringAccept = (cateringID) => {
        const tempCalendarListObj = cloneDeep(upcomingCateringListObj);
        const acceptedRequestIndex = tempCalendarListObj['awaiting'].data.findIndex((item) => item.id === cateringID);

        // Add item to upcoming (accepted) list
        const acceptedItem = Object.assign({}, tempCalendarListObj['awaiting'].data[acceptedRequestIndex], { parent_status: 1, child_status: 1 });
        const tempObj = Object.assign({}, acceptedItem, { timeObj: checkDate(acceptedItem, true) });

        tempCalendarListObj['upcoming'].data.push(tempObj);
        tempCalendarListObj['upcoming'].data.sort((a, b) => (a.timeObj.startTime - b.timeObj.startTime));

        // Remove item from Awaiting list
        if (acceptedRequestIndex !== -1) {
            tempCalendarListObj['awaiting'].data.splice(acceptedRequestIndex, 1);
        }

        // Set data to state
        setUpcomingCateringListObj(tempCalendarListObj);
    };

    const onCateringDecline = (cateringID) => {
        const tempCalendarListObj = cloneDeep(upcomingCateringListObj);
        const declinedRequestIndex = tempCalendarListObj['awaiting'].data.findIndex((item) => item.id === cateringID);
        if (declinedRequestIndex !== -1) {
            tempCalendarListObj['awaiting'].data.splice(declinedRequestIndex, 1);
        }
        setUpcomingCateringListObj(tempCalendarListObj);
    };

    const onCateringHired = (hiredData, wasHired = true) => {
        let isPast = hiredData.timeObj.startTime < moment().unix(),
            cateringID = hiredData.id,
            tempCalendarListObj = cloneDeep(isPast ? pastCateringListObj : upcomingCateringListObj),
            hiredRequestIndex = tempCalendarListObj['upcoming'].data.findIndex((item) => item.id === cateringID);

        // Search if catering already in either Not Hired or Hired list
        let cateringIndexInHired = tempCalendarListObj['hired'].data.findIndex((item) => item.id === cateringID),
            cateringIndexInNotHired = tempCalendarListObj['notHired'].data.findIndex((item) => item.id === cateringID),
            tempObj = Object.assign(cloneDeep(hiredData), { timeObj: checkDate(hiredData, true) });

        // Remove the catering from either Not Hired or Hired list first
        // because sometimes the portal will change a lead from Hired -> Not Hired or vice-versa
        (cateringIndexInHired !== -1) && tempCalendarListObj['hired'].data.splice(cateringIndexInHired, 1);
        (cateringIndexInNotHired !== -1) && tempCalendarListObj['notHired'].data.splice(cateringIndexInNotHired, 1);

        // Add catering to the list again
        tempCalendarListObj[wasHired ? 'hired' : 'notHired'].data.push(tempObj);
        tempCalendarListObj[wasHired ? 'hired' : 'notHired'].data.sort((a, b) => (a.timeObj.startTime - b.timeObj.startTime));

        // Remove this catering from Accepted list if existed there
        (hiredRequestIndex !== -1) && tempCalendarListObj['upcoming'].data.splice(hiredRequestIndex, 1);

        // Set data to state
        (isPast) ? setPastCateringListObj(tempCalendarListObj) : setUpcomingCateringListObj(tempCalendarListObj);
    };
    // endregion

    // region Function - On Catering Confirmation Responded
    useEffect(() => {
        if (triggerCateringResponded) {
            console.log('triggerCateringResponded', triggerCateringResponded);
            const updatedCateringData = cloneDeep(triggerCateringResponded);

            // Reset flag
            setTriggerCateringResponded(null);

            // Start processing
            setTimeout(() => {
                let isPast = updatedCateringData.timeObj.startTime < moment().unix(),
                    cateringID = updatedCateringData.id,
                    tempCalendarListObj = cloneDeep(isPast ? pastCateringListObj : upcomingCateringListObj),
                    cateringIndex = tempCalendarListObj['hired'].data.findIndex((item) => item.id === cateringID);

                if (cateringIndex !== -1) {
                    tempCalendarListObj['hired'].data[cateringIndex]['confirmed_status'] = updatedCateringData['confirmed_status'];
                }

                // Sort
                tempCalendarListObj['hired'].data.sort((a, b) => (Number(isFeedbackNeeded(b)) - Number(isFeedbackNeeded(a))));

                // Get feedback needed count
                setFeedbackNeedCount(getFeedbackNeededLeads(tempCalendarListObj['hired'].data));

                // Save catering data to state
                (isPast) ? setPastCateringListObj(tempCalendarListObj) : setUpcomingCateringListObj(tempCalendarListObj);
            }, 500);
        }
    }, [triggerCateringResponded, pastCateringListObj, upcomingCateringListObj, cateringScreenKey]);
    // endregion

    // region Function - Past Catering Update Helpers
    const _onHiredArrUpdated = (newHiredArr) => {
        const tempListObj = cloneDeep(pastCateringListObj);
        tempListObj['hired'].data = newHiredArr;
        setFeedbackNeedCount(getFeedbackNeededLeads(newHiredArr));
        setPastCateringListObj(tempListObj);
    };

    const _onPastDataUpdated = (newPastDataObj = {}) => {
        const tempListObj = cloneDeep(newPastDataObj);
        setPastCateringListObj(tempListObj);
        setFeedbackNeedCount(getFeedbackNeededLeads(tempListObj['hired'].data));
    };
    // endregion

    // region Function - On Catering Feedback Submitted
    const onCateringFeedbackSubmitted = (updatedCatering) => {
        let tempArr = cloneDeep(pastCateringListObj['hired'].data),
            cateringIndex = tempArr.findIndex((item) => item.id === updatedCatering.id);

        if (cateringIndex > -1) {
            tempArr.splice(cateringIndex, 1, updatedCatering);
            _onHiredArrUpdated(tempArr);
        }
    };
    // endregion

    // region Function - On Catering Not Found
    const onCateringNotFound = (cateringID) => {
        let tempPendingArr = cloneDeep(upcomingCateringListObj['awaiting']?.data || []),
            tempAcceptedArr = cloneDeep(upcomingCateringListObj['upcoming']?.data || []),
            tempHiredArr = cloneDeep(upcomingCateringListObj['hired']?.data || []),
            tempFullData = cloneDeep(cateringData);

        let fullDataIndex = tempFullData.findIndex((item) => Number(item.id) === cateringID),
            pendingIndex = tempPendingArr.findIndex((item) => Number(item.id) === cateringID),
            acceptedIndex = tempAcceptedArr.findIndex((item) => Number(item.id) === cateringID),
            hiredIndex = tempHiredArr.findIndex((item) => Number(item.id) === cateringID);

        if (fullDataIndex > -1) {
            tempFullData.splice(fullDataIndex, 1);
        }
        if (pendingIndex > -1) {
            tempPendingArr.splice(pendingIndex, 1);
        }
        if (acceptedIndex > -1) {
            tempAcceptedArr.splice(acceptedIndex, 1);
        }
        if (hiredIndex > -1) {
            tempHiredArr.splice(hiredIndex, 1);
        }

        const tempListObj = cloneDeep(upcomingCateringListObj);

        if (tempListObj['awaiting']) {
            tempListObj['awaiting'].data = tempPendingArr;
        }
        if (tempListObj['upcoming']) {
            tempListObj['upcoming'].data = tempAcceptedArr;
        }
        if (tempListObj['hired']) {
            tempListObj['hired'].data = tempHiredArr;
        }

        setUpcomingCateringListObj(tempListObj);
        setCateringData(tempFullData);
    };
    // endregion

    // region Function - On Hood Confirmation Responded
    const onHoodConfirmationResponded = (newHoodItem) => {
        const tempHoodList = cloneDeep(hoodData);
        const hoodIndex = tempHoodList.findIndex((item) => item.id === newHoodItem.id);

        if (hoodIndex !== -1) {
            Object.assign(tempHoodList[hoodIndex], newHoodItem);
        }

        setHoodData(tempHoodList);
    };
    // endregion

    // region Function - On Hood Feedback Submitted
    const onHoodFeedbackSubmitted = (hoodData) => {
        let hoodID = hoodData.id,
            tempHoodList = cloneDeep(hoodData),
            hoodIndex = tempHoodList.findIndex((item) => item.id === hoodID);

        if (hoodIndex !== -1) {
            tempHoodList[hoodIndex]['weather'] = hoodData['thisTruckData']['weather'];
        }

        setHoodData(tempHoodList);
    };
    // endregion

    // region Function - On Catering Item Pressed
    const _onCateringItemPressed = (cateringData) => {
        RootNavigation.push(RouteKeys.CATERING_DETAILS, {
            cateringData,
            onAccept: (cateringID) => onCateringAccept(cateringID),
            onDecline: (cateringID) => onCateringDecline(cateringID),
            onHired: (hiredData, wasHired) => onCateringHired(hiredData, wasHired),
            onFeedbackSubmitted: (updatedCatering) => onCateringFeedbackSubmitted(updatedCatering),
            onLeadNotFound: (cateringID) => onCateringNotFound(cateringID),
            onMyLeadUpdated: (newData) => {
                const categoryKey = newData['categoryKey'];
                const tempCalendarListObj = cloneDeep(upcomingCateringListObj);
                const cateringIndex = tempCalendarListObj[categoryKey].data.findIndex((item) => item.id === newData.id);
                if (cateringIndex !== -1) {
                    tempCalendarListObj[categoryKey].data.splice(cateringIndex, 1, newData);
                    setUpcomingCateringListObj(tempCalendarListObj);
                }
            }
        });
    };
    // endregion

    // region Function - On Hood Item Pressed
    const _onHoodItemPressed = (hoodData) => {
        RootNavigation.push(RouteKeys.HOOD_DETAILS, {
            hoodData,
            onFeedbackSubmitted: (updatedHoodData) => onHoodFeedbackSubmitted(updatedHoodData),
            onConfirmationResponded: (updatedHoodItem) => onHoodConfirmationResponded(updatedHoodItem)
        });
    };
    // endregion

    // region Function - On Past Button Pressed
    const _onPastButtonPressed = (isHood = false) => {
        if (isHood) {
            RootNavigation.navigate(RouteKeys.HOOD_PAST, { renderSectionItem });
        }
        else {
            RootNavigation.navigate(RouteKeys.CATERING_PAST, {
                pastCateringCategories: cateringHistoryCategories,
                renderSectionHeader,
                renderSectionItem,
                onHiredArrUpdated: (newObj) => _onHiredArrUpdated(newObj),
                onPastDataUpdated: (newObj) => _onPastDataUpdated(newObj)
            });
        }
    };
    // endregion

    // region Render Toolbar
    const renderTabButton = (text = '', isActive = false, onPress = () => {
    }) => (
        <TouchableOpacity
            onPress={onPress}
            style={{
                height: 56, flex: 1, alignItems: 'center', justifyContent: 'center',
                backgroundColor: isActive ? 'white' : 'transparent',
                borderRadius: 8
            }}>
            <Text style={{ fontSize: 18, fontWeight: isActive ? 'bold' : 'normal' }}>
                {text}
            </Text>
        </TouchableOpacity>
    );

    const renderToolbar = () => {
        const isHood = !cateringTabActive;
        const feedbackCount = isHood ? hoodFeedbackNeedCount : feedbackNeedCount;
        const pastAcceptedCateringCount = isHood ? 0 : pastAcceptedLeadCount;

        return(
            <View style={{ paddingTop: 24, width: '100%', backgroundColor: 'white', paddingBottom: 24, borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                {/* Segment Control */}
                <View style={{ width: '70%', backgroundColor: GLOBAL.COLOR.BASIC.GRAY_OPAQUE, flexDirection: 'row', alignSelf: 'center', borderRadius: 8, padding: 4 }}>
                    {renderTabButton('Catering', cateringTabActive, () => {
                        setCateringTabActive(true);
                        cateringTab.current?.fadeZoom(600);
                    })}
                    {renderTabButton('Truckster Hoods', !cateringTabActive, () => {
                        setCateringTabActive(false);
                        hoodTab.current?.fadeZoom(600);
                    })}

                    {
                        isLoading &&
                        <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}/>
                    }
                </View>

                {/* Title */}
                <View style={{ flexDirection: 'row', alignItems: 'center', width: '70%', marginTop: 24, alignSelf: 'center' }}>
                    {/* Title */}
                    <Text style={{ fontWeight: '900', fontSize: 28, letterSpacing: -0.4, marginRight: 16 }}>
                        Upcoming {(isHood ? 'Hoods' : 'Leads')}
                    </Text>

                    {/* Reload Button */}
                    {
                        !isLoading &&
                        <TouchableOpacity onPress={() => goFetchCateringData()}>
                            <Feather name={'refresh-cw'} color={GLOBAL.COLOR.TEXT_GRAY} size={24}/>
                        </TouchableOpacity>
                    }

                    {/* Blank Space */}
                    <View style={{ flex: 1 }}/>

                    {/* Past Button */}
                    {
                        (!isLoading && hasCateringAccess && (hasHoodAccess ? true : cateringTabActive) && (isHood ? (pastHoodData.length > 0) : (pastCateringData.length > 0))) &&
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
                            <TouchableOpacity
                                onPress={() => _onPastButtonPressed(isHood)}
                                style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.DODGER_BLUE }}>
                                    Past {isHood ? 'Hoods' : 'Leads'}
                                </Text>
                            </TouchableOpacity>

                            {/* Item Count */}
                            {
                                (feedbackCount + pastAcceptedCateringCount) > 0 &&
                                <View style={{
                                    width: 28, height: 28, borderRadius: 14, marginLeft: 6,
                                    alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: GLOBAL.COLOR.MAIN
                                }}>
                                    <Text style={{ fontWeight: '500', color: 'white', fontSize: 14, marginBottom: 1 }}>
                                        {feedbackCount + pastAcceptedCateringCount}
                                    </Text>
                                </View>
                            }
                        </View>
                    }
                </View>
            </View>
        );
    };
    // endregion

    // region Render Section Header
    const renderSectionHeader = (text, count = 0, onPress = null, highlightCount = false) => (
        <TouchableOpacity
            disabled={!onPress}
            onPress={onPress}
            style={{
                width: '100%', backgroundColor: 'white',
                paddingLeft: 24, paddingRight: 16, height: 56, alignItems: 'center', flexDirection: 'row',
                borderBottomWidth: 1, borderTopWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER,
                marginTop: 8
            }}>
            {/* Title */}
            <Text style={{ color: 'black', fontSize: 20, fontWeight: 'bold', flex: 1 }}>
                {text}
            </Text>

            {/* Item Count */}
            <View style={{
                width: 32, height: 32, borderRadius: 16, marginHorizontal: 8,
                alignItems: 'center', justifyContent: 'center',
                backgroundColor: highlightCount ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.BACKGROUND_GREY
            }}>
                <Text style={{ fontWeight: 'bold', color: highlightCount ? 'white' : 'black', fontSize: 13 }}>
                    {count || 0}
                </Text>
            </View>
        </TouchableOpacity>
    );
    // endregion

    // region Render Section Item
    const renderSectionItem = (item, isHood = false, customFunc = null) => {
        const sectionTitle = item.sectionTitle, sectionColor = item.sectionColor;

        function customTemplate() {
            return this.duration.asSeconds() >= 86400 ? 'd [days]' : (this.duration.asSeconds() >= 3600 ? 'h [hours]' : (this.duration.asSeconds() >= 60 ? 'm [minutes]' : 's [seconds]'));
        }

        let claimStatusComp = <SceneCateringClaimStatusTag cateringData={item}/>,
            isPast = item.timeObj?.endTime < moment().unix(),
            isLastMinute = (item['type'] === 1) && sectionTitle === 'Pending',
            needConfirmation =
                (isHood ? true : (sectionTitle === 'Hired')) &&
                ((isHood ? item['thisTruckData']?.['confirmed_status'] : item['confirmed_status']) === 0) &&
                (item.timeObj?.startTime >= moment().unix()) &&
                moment.unix(item.timeObj?.startTime).isSame(moment(), 'week');

        let daysToEvent = item.timeObj?.startTime - moment().unix(),
            daysLeftToGrab = (5 * 86400) - (moment().unix() - moment.utc(item['truck_received_at'], 'YYYY-MM-DD HH:mm:ss').unix()),
            daysLeft = daysToEvent < daysLeftToGrab ? daysToEvent : daysLeftToGrab;

        let hideContactInfo = isPast && DATA.hidePastHoodContactFrom.includes(GLOBAL.DATA.USER.id);

        const isNotGoing = isHood ? (item['thisTruckData']?.['confirmed_status'] === 2) : (item['confirmed_status'] === 2);

        const dateTimeViewWidth = 80;

        return(
            <TouchableOpacity
                key={`cateringItem_${item.id}`}
                onPress={
                    customFunc ?
                        customFunc
                        :
                        (() => isHood ? _onHoodItemPressed(item) : _onCateringItemPressed(item))
                }
                style={{ width: '100%', backgroundColor: isLastMinute ? GLOBAL.COLOR.BASIC.GOLD : 'white' }}>
                {/* 5-day Countdown */}
                {
                    (sectionTitle === 'Pending') &&
                    <View style={{ width: '100%', paddingHorizontal: 24, paddingVertical: 12, backgroundColor: GLOBAL.COLOR.MAIN }}>
                        <Text style={{ fontSize: 16, color: 'white', fontWeight: '500', textAlign: 'center' }}>
                            {moment.duration(daysLeft, 'seconds').format(customTemplate, { trim: false })} left to grab this lead
                        </Text>
                    </View>
                }

                {/* Main Content */}
                <View style={{
                    flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch',
                    paddingRight: 16, paddingTop: 16, paddingBottom: isHood ? 16 : (claimStatusComp ? 8 : 16)
                }}>
                    {/* Date String */}
                    <View style={{ alignItems: 'center', width: dateTimeViewWidth }}>
                        {/* Month */}
                        <Text style={{ fontSize: 16, fontWeight: 'bold', color: (isLastMinute) ? GLOBAL.COLOR.BASIC.ORANGE : GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY }}>
                            {moment.unix(item.timeObj.startTime).format('MMM')}
                        </Text>

                        {/* Year */}
                        <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 22, letterSpacing: -0.2, marginTop: 4 }}>
                            {moment.unix(item.timeObj.startTime).format('DD')}
                        </Text>
                    </View>

                    {/* Event Info */}
                    <View style={{ flex: 1, justifyContent: 'flex-start' }}>
                        {/* Lead Type and ID */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                            {/* Lead Type Tag */}
                            <SceneCateringTagItem
                                style={{ marginTop: 0 }}
                                text={!item.source ? 'Truckster Lead' : 'My Lead'}
                                backgroundColor={!item.source ? GLOBAL.COLOR.BASIC.DARK_BLUE : GLOBAL.COLOR.BACKGROUND_GREY}
                                textColor={!item.source ? 'white' : GLOBAL.COLOR.TEXT_GRAY}
                            />

                            {/* Catering ID */}
                            {
                                (isDevMode || isTestAccount(GLOBAL.DATA.USER.email)) &&
                                <SceneCateringTagItem
                                    style={{ marginTop: 0, marginBottom: 8 }}
                                    text={'ID: ' + item.id}
                                    backgroundColor={'rgba(0, 117, 219, 0.2)'}
                                    textColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                                />
                            }
                        </View>

                        {/* Time */}
                        <Text style={{ fontSize: 16, fontWeight: '500', color: isPast ? GLOBAL.COLOR.BASIC.NEUTRAL_GREY : GLOBAL.COLOR.MAIN }}>
                            {moment.unix(item.timeObj.startTime).format('ddd, h:mm A') + ' - ' + moment.unix(item.timeObj?.endTime).format('h:mm A')}
                        </Text>

                        {/* Occasion Name */}
                        <Text style={{ fontSize: 22, fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginTop: 8, marginRight: 8 }}>
                            {(hideContactInfo && isHood) ? `${isHood ? 'Hood' : 'Catering'} Event` : (isHood ? item.location?.name : item['occasion'])}
                        </Text>

                        {/* Requested Person */}
                        <Text style={{ fontWeight: '400', color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 16, marginTop: 8 }} numberOfLines={1}>
                            {isHood ? item.location?.category : item['name']}
                        </Text>
                    </View>

                    {/* Hire Status */}
                    {
                        !isHood &&
                        <View style={{ paddingVertical: 10, paddingHorizontal: 14, backgroundColor: sectionColor, borderRadius: 6, marginTop: 6, marginRight: 8 }}>
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: '500' }}>
                                {sectionTitle}
                            </Text>
                        </View>
                    }
                </View>

                {/* Catering Status */}
                {
                    (!isHood && !isPast) &&
                    <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', marginTop: claimStatusComp ? 8 : 0, marginLeft: dateTimeViewWidth }}>
                        {/* Claim Status */}
                        {claimStatusComp}
                    </View>
                }

                {/* Confirmation Needed - For Hired leads only */}
                {
                    (needConfirmation || isNotGoing) &&
                    <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', marginLeft: dateTimeViewWidth, marginBottom: 16, paddingTop: 8 }}>
                        {
                            isNotGoing ?
                                <SceneCateringTagItem
                                    style={{ marginTop: 0 }}
                                    text={'NOT GOING'}
                                    backgroundColor={GLOBAL.COLOR.BASIC.RED}
                                    textColor={'white'}
                                    iconName={'cancel'}
                                />
                                :
                                <SceneCateringTagItem
                                    style={{ marginTop: 0 }}
                                    text={'CONFIRMATION REQUIRED'}
                                    backgroundColor={'rgba(250, 57, 61, 0.2)'}
                                    textColor={GLOBAL.COLOR.MAIN}
                                />
                        }
                    </View>
                }

                {/* Feedback Needed - for Confirmed/Can't Go Leads only */}
                {
                    isFeedbackNeeded(isHood ? item['thisTruckData'] : item) &&
                    <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', marginLeft: dateTimeViewWidth, marginVertical: 8 }}>
                        <SceneCateringTagItem
                            style={{ marginTop: 0 }}
                            text={'FEEDBACK NEEDED'}
                            backgroundColor={'rgba(250, 57, 61, 0.2)'}
                            textColor={GLOBAL.COLOR.MAIN}
                        />
                    </View>
                }
            </TouchableOpacity>
        );
    };
    // endregion

    // region Render Empty View
    const renderEmptyView = (isHood = false) => {
        const lottieSize = isHood ? 400 : 180;
        return(
            <Animatable.View
                animation={'fadeIn'}
                style={{ alignItems: 'center', justifyContent: 'center', backgroundColor: isHood ? 'white' : GLOBAL.COLOR.BACKGROUND_GREY, flex: 1 }}>
                {/* Lottie */}
                <View style={{ alignItems: 'center', justifyContent: 'center', width: lottieSize, height: lottieSize }}>
                    {
                        isLoading ?
                            <Bounce size={56} color={GLOBAL.COLOR.MAIN}/>
                            :
                            <TouchableOpacity onPress={() => goFetchCateringData()}>
                                <Lottie
                                    options={{
                                        loop: true,
                                        autoplay: true,
                                        animationData: isHood ? HoodLottie : EmptyCalendarLottie,
                                        rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
                                    }}
                                    width={lottieSize}
                                    height={lottieSize}
                                    isStopped={false}
                                    isPaused={false}
                                />
                            </TouchableOpacity>
                    }
                </View>

                {/* Text */}
                <View style={{ alignItems: 'center' }}>
                    <EmptyTextSection
                        title={`You have no\n${isHood ? 'hood events' : 'catering requests'} yet`}
                        subTitle={'Strengthen your food truck profile so potential clients can easily find you and get to know you better'}
                    />

                    <Button
                        onPress={() => {
                            RootNavigation.jumpTo(RouteKeys.PROFILE_STACK);
                            setGlobalState(STATE_KEYS.CURRENT_TAB_STACK, RouteKeys.PROFILE_STACK);
                        }}
                        text={'Fill Your Profile'}
                        textStyle={{ color: 'white' }}
                        style={{ backgroundColor: 'black', width: 300, marginTop: 24, alignSelf: 'center' }}
                    />
                </View>

                {/* New Lead Button */}
                {renderAddLeadButton()}
            </Animatable.View>
        );
    };
    // endregion

    // region Render Add Lead Button
    const renderAddLeadButton = () => (
        <Animatable.View
            animation={'zoomIn'}
            delay={500}
            duration={600}
            style={{ position: 'absolute', bottom: 24, right: 36 }}>
            <TouchableOpacity
                onPress={() => RootNavigation.push(RouteKeys.CATERING_EXTERNAL_LEAD, { onLeadCreated: (lead) => onExternalLeadCreated(lead) })}
                style={{
                    backgroundColor: 'black', borderRadius: 12,
                    alignSelf: 'center', boxShadow: '0px 1px 5px #aeafb1, 0px -1px 5px #aeafb1',
                    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16
                }}>
                <Feather name={'plus'} size={24} color={'white'}/>
                <Text style={{ color: 'white', fontSize: 18, fontWeight: '500', marginLeft: 8 }}>
                    Add Lead
                </Text>
            </TouchableOpacity>
        </Animatable.View>
    );
    // endregion

    // region Render Catering Tab
    const renderCateringTab = () => {
        const cateringSections = (
            cateringData.length ?
                Object.keys(cateringCategories).map((key) => {
                    const sectionData = upcomingCateringListObj[key];
                    const color = cateringCategories[key].color, title = cateringCategories[key].title;
                    return({
                        title,
                        data: sectionData.data.map((item) => Object.assign(cloneDeep(item), { sectionColor: color, sectionTitle: title }))
                    });
                })
                :
                []
        );

        return(
            hasCateringAccess ?
                (
                    cateringData.length ?
                        <View style={{ flex: 1, width: '100%' }}>
                            {/* Main List */}
                            <SectionList
                                style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                                showsVerticalScrollIndicator={true}
                                contentContainerStyle={{ paddingBottom: 24 + 56, paddingHorizontal: '15%' }}
                                sections={cateringSections}
                                renderItem={({ item }) => renderSectionItem(item)}
                                renderSectionHeader={({ section }) => renderSectionHeader(section.title, section.data.length)}
                                keyExtractor={(item) => item.id?.toString()}
                                removeClippedSubviews={true}
                                maxToRenderPerBatch={20}
                                ItemSeparatorComponent={() => <View style={{ height: 1, width: '100%', backgroundColor: GLOBAL.COLOR.DIVIDER }}/>}
                            />

                            {/* New Lead Button */}
                            {renderAddLeadButton()}
                        </View>
                        :
                        renderEmptyView()
                )
                :
                <TierBlockView
                    lottieFile={CateringLottie}
                    lottieSize={240}
                    sectionBenefit={'Catering scheduling for weddings, office lunches, graduations, private parties, and more'}
                    availableTier={1}
                />

        );
    };
    // endregion

    // region Render Hood Tab
    const renderHoodTab = () => (
        hasHoodAccess ?
            (
                hoodData.length ?
                    <FlatList
                        data={hoodData}
                        style={{ flex: 1, width: '100%' }}
                        contentContainerStyle={{ paddingVertical: 8, width: '70%', alignSelf: 'center' }}
                        keyExtractor={(item) => item.id?.toString()}
                        renderItem={({ item }) => renderSectionItem(item, true)}
                        ItemSeparatorComponent={() => <View style={{ height: 1, width: '100%', backgroundColor: GLOBAL.COLOR.DIVIDER }}/>}
                    />
                    :
                    renderEmptyView(true)
            )
            :
            <TierBlockView
                lottieFile={HoodLottie}
                lottieSize={240}
                sectionBenefit={'Catering schedule at neighborhoods, apartment complexes, office buildings, or business parks'}
                availableTier={2}
            />
    );
    // endregion

    // region MAIN RENDER
    return(
        isProfilePendingApproval() ?
            <Animatable.View animation={'fadeZoom'} style={{ width: '100%', flex: 1 }}>
                {/* Nav Bar */}
                <Text style={{ color: 'gray', fontSize: 24, fontWeight: 'bold', marginTop: 24 }}>
                    Catering
                </Text>

                {/* Text */}
                <View style={{ flex: 1, width: '100%', backgroundColor: 'white', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 56 }}>
                    <Text style={{ textAlign: 'center', fontSize: 18, color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY }}>
                        This feature will become available once your profile is approved
                    </Text>
                </View>
            </Animatable.View>
            :
            <Animatable.View
                animation={'fadeZoom'}
                style={{ width: '100%', flex: 1 }}>
                {/* Toolbar */}
                {renderToolbar()}

                {/* Main Content */}
                {
                    (isLoading && !cateringData.length) ?
                        <LoadingView style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}/>
                        :
                        <View style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                            {/* Loading Indicator */}
                            {
                                isLoading &&
                                <View style={{ alignSelf: 'center', marginVertical: 16 }}>
                                    <ActivityIndicator size={32} color={GLOBAL.COLOR.TEXT_GRAY}/>
                                </View>
                            }

                            {/* Catering View */}
                            <Animatable.View
                                ref={cateringTab}
                                animation={'fadeZoom'}
                                style={[{ width: '100%', flex: 1, display: 'none' }, cateringTabActive && { display: 'flex' }]}>
                                {renderCateringTab()}
                            </Animatable.View>

                            {/* Hood View */}
                            <Animatable.View
                                ref={hoodTab}
                                animation={'fadeZoom'}
                                style={[{ width: '100%', flex: 1, display: 'none' }, !cateringTabActive && { display: 'flex' }]}>
                                {renderHoodTab()}
                            </Animatable.View>
                        </View>
                }
            </Animatable.View>
    );
    // endregion
}
